import React, { useCallback, useEffect, useState } from 'react';
import classNames from 'classnames';
import './App.less';
import { Sidebar } from 'semantic-ui-react';
import { ModuleEnum } from './utils/enums';
import { RouteToModule } from './utils/routes';
import { moduleNameToEnum } from './utils/helpers';
import { RouteComponentProps, useLocation } from 'react-router-dom';
import { POSSidebar, useHttp } from '@publicisgroupe/p-os-components';
import { useUserContext } from './contexts/AuthProvider';
import {
  Claims,
  Dictionary,
  NavigationItemDto,
  Notification,
  SignalRNotification,
} from './types/types';
import useWebframePermissions from './hooks/useWebframePermissions';
import NoAccess from './components/NoAccess';
import { HelpSection } from 'components/HelpSection/HelpSection';
import { UserProfileSection } from 'components/UserProfileSection/UserProfileSection';
import { canShowCookieBanner } from 'components/CookieBannerModal/CookieBannerHelper';
import { CookieBannerModal } from 'components/CookieBannerModal/CookieBannerModal';
import { GlobalNotification } from 'components/GlobalNotification/GlobalNotification';
import CockpitWidgetNavigation from 'components/CockpitWidgetNavigation';
import WebframePageLimited from 'pages/WebframePageLimited';
import { WebframePage } from 'pages/WebframePageLimited/WebframePage';
import { ModuleCommunication } from 'components/ModuleCommunication/ModuleCommunication';
import * as signalR from '@microsoft/signalr';
import { HubConnection } from '@microsoft/signalr';
import { notificationApi } from './api/notificationApi';
import { AxiosResponse } from 'axios';
import { useEnvironment } from '@publicisgroupe/p-os-components/build/contexts/EnvironmentContext';
import { POS_NOTIFICATION_REDIRECTION_MESSAGE } from 'utils/constants';
import { PosNotificationsHandler } from 'components/PosNotificationsHandler/PosNotificationsHandler';
import { useNavigationFilters } from 'hooks/useNavigationFilters';
import './i18n';
import { FeatureFlagsProvider } from './components/FeatureFlagsProvider';
import ThemesProvider from 'components/ThemesProvider';
import { usePendoIntegration } from './integrations/pendo/usePendoIntegration';

export const NotificationContext = React.createContext<HubConnection | undefined>(undefined);

function App(props: Readonly<RouteComponentProps>) {
  const { settings } = useEnvironment();

  const { history } = props;
  const [filters, setFilters] = useState<NavigationItemDto[]>([]);
  const [filterNames, setFilterNames] = useState<string[]>([]);
  const location = useLocation();
  const { userInfo, navData } = useUserContext();
  const { isGranted, isLoading: isPermissionsLoading } = useWebframePermissions();
  const [openHelpSection, setOpenHelpSection] = useState<boolean>(false);
  const [openUserProfileSettings, setOpenUserProfileSettings] = useState<boolean>(false);
  const [openNotificationSidebar, setOpenNotificationSidebar] = useState<boolean>(false);
  const [showCookieBannerModal, setShowCookieBannerModal] = useState<boolean>(false);
  const [showCookieSettings, setShowCookieSettings] = useState<boolean>(false);
  // Extract notification/websocket handling to it's own file
  // https://dev.azure.com/TITAN-Products-Publicis/Publicis%20OS/_workitems/edit/6153
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadNotifications, setUnreadNotifications] = useState<Notification[]>([]);
  const http = useHttp('notifications');
  usePendoIntegration();

  const [websocketConnection, setWebsocketConnection] = useState<HubConnection | undefined>(
    undefined
  );

  const handleHelpSectionSidebar = useCallback((val: boolean) => {
    setOpenHelpSection(val);
  }, []);

  const handleUserProfileSettingsSidebar = useCallback((val: boolean) => {
    setOpenUserProfileSettings(val);
  }, []);

  // Extract notification/websocket handling to it's own file
  // https://dev.azure.com/TITAN-Products-Publicis/Publicis%20OS/_workitems/edit/6153
  const handleNotificationSidebar = useCallback(
    (val: boolean) => {
      setOpenNotificationSidebar(val);
      if (val && unreadNotifications.length > 0) {
        setUnreadNotifications([]);
        notificationApi(http)
          ?.markNotificationsRead(unreadNotifications.map((x) => x.id))
          .then()
          .catch((error) => {
            console.error(error);
          });
      }
    },
    [http, unreadNotifications]
  );

  const handleNotificationOnClose = (messageId: string) => {
    notificationApi(http)
      ?.deleteNotification(messageId)
      .then(() => {
        setNotifications(notifications.filter((notification) => notification.id !== messageId));
      })
      .catch((error) => {
        console.error(error);
      });
  };
  const handleNotificationClick = (notificationData: Dictionary<string>) => {
    const module = notificationData['Module'];
    const infoData = {
      message: POS_NOTIFICATION_REDIRECTION_MESSAGE,
      module: module,
      data: notificationData,
    };

    handleWidgetNavigation(moduleNameToEnum(module));

    setTimeout(() => {
      window.postMessage(infoData, window.location.origin);
    }, 3000);
  };

  const handleCookieSettingsModal = useCallback((val?: boolean) => {
    setShowCookieSettings(val ? val : false);
  }, []);
  // need externalApprover claims in permissions api, currently not configured
  const limitedAccess =
    userInfo.claims && userInfo.claims.some((x) => x == Claims.ExternalApprover);

  const handleWidgetNavigation = useCallback(
    (module: ModuleEnum, queryParams?: string) => {
      history.push({
        pathname: RouteToModule(module),
        search: queryParams && queryParams !== '' ? location.search + queryParams : location.search,
      });
    },
    [history, location.search]
  );

  // Extract notification/websocket handling to it's own file
  // https://dev.azure.com/TITAN-Products-Publicis/Publicis%20OS/_workitems/edit/6153
  useEffect(() => {
    const url = settings.apiBaseURL + '/notifications';
    const connection = new signalR.HubConnectionBuilder()
      .withUrl(url, {
        accessTokenFactory: () => window.bearerToken as string,
        withCredentials: false,
        headers: {
          'Ocp-Apim-Subscription-Key': settings.ocpApimKey,
        },
      })
      .build();
    connection.start().catch((error) => {
      console.error(error);
    });

    setWebsocketConnection(connection);

    notificationApi(http)
      ?.getNotifications()
      .then((response: AxiosResponse<Notification[]>) => {
        setUnreadNotifications(response.data.filter((notification: any) => !notification.read));
        setNotifications(response.data);
      })
      .catch((error) => {
        console.error(error);
      });
  }, [http, settings.apiBaseURL, settings.ocpApimKey]);

  useEffect(() => {
    if (!websocketConnection) {
      return;
    }

    websocketConnection.on('newMessage', (message: SignalRNotification) => {
      const newMessage = {
        id: message.Id,
        title: message.Title,
        message: message.Message,
        context: message.Context,
        read: message.Read,
        createdAt: message.CreatedAt,
      };

      if (!notifications.some((notification) => notification.id === newMessage.id)) {
        setNotifications([newMessage, ...notifications]);
        if (!unreadNotifications.some((notification) => notification.id === newMessage.id)) {
          setUnreadNotifications([newMessage, ...unreadNotifications]);
        }
      }
    });

    return () => {
      websocketConnection.off('newMessage');
    };
  }, [notifications, unreadNotifications, websocketConnection]);

  const { selectedFiltersChanged, errorInQueryParams } = useNavigationFilters(
    history,
    filters,
    filterNames,
    limitedAccess,
    setFilterNames
  );

  // init
  useEffect(() => {
    if (limitedAccess) return;
    setShowCookieBannerModal(canShowCookieBanner());

    if (navData.length > 0) {
      const filterNamesData = navData[0].filters;
      const filtersData = navData ?? [];

      setFilterNames(filterNamesData);
      setFilters(filtersData);
    }
  }, [limitedAccess, navData]);

  return (
    <>
      <ModuleCommunication />
      <CockpitWidgetNavigation history={history} navigateToWidget={handleWidgetNavigation} />
      <PosNotificationsHandler />
      {!isPermissionsLoading && websocketConnection && (
        <NotificationContext.Provider value={websocketConnection}>
          <FeatureFlagsProvider>
            <ThemesProvider>
              <div className="webframe">
                {isGranted('Webframe') ? (
                  <>
                    <POSSidebar
                      visible={openHelpSection}
                      onSetVisibility={(val: boolean) => handleHelpSectionSidebar(val)}
                      direction={'right'}
                      width={'wide'}
                      animation={'overlay'}
                      sidebarContent={
                        <>
                          <HelpSection
                            handleHelpSection={(val: boolean) => handleHelpSectionSidebar(val)}
                          />
                        </>
                      }
                    />
                    <div className="user-profile">
                      <POSSidebar
                        inverted
                        visible={openUserProfileSettings}
                        onSetVisibility={(val: boolean) => handleUserProfileSettingsSidebar(val)}
                        direction={'right'}
                        width={'wide'}
                        animation={'overlay'}
                        sidebarContent={
                          <>
                            <UserProfileSection
                              handleUserProfileSettingsSection={(val: boolean) =>
                                handleUserProfileSettingsSidebar(val)
                              }
                              handleCookieBanner={() => {
                                setOpenUserProfileSettings(false);
                                setShowCookieBannerModal(true);
                                setShowCookieSettings(true);
                              }}
                            />
                          </>
                        }
                      />
                    </div>

                    <div className="news-feed-section">
                      <POSSidebar
                        inverted
                        visible={openNotificationSidebar}
                        onSetVisibility={(val: boolean) => handleNotificationSidebar(val)}
                        direction={'right'}
                        width={'wide'}
                        animation={'overlay'}
                        sidebarContent={
                          <GlobalNotification
                            handleNotificationSidebar={() => handleNotificationSidebar(false)}
                            handleNotificationOnClose={handleNotificationOnClose}
                            messages={notifications}
                            handleNotificationClick={(data: Dictionary<string>) =>
                              handleNotificationClick(data)
                            }
                          />
                        }
                      />
                    </div>
                    <div
                      className={classNames({
                        pushable:
                          openHelpSection || openUserProfileSettings || openNotificationSidebar,
                      })}
                    >
                      <Sidebar.Pusher
                        dimmed={
                          openHelpSection || openUserProfileSettings || openNotificationSidebar
                        }
                      >
                        {limitedAccess && (
                          <WebframePageLimited
                            handleHelpSectionSidebar={handleHelpSectionSidebar}
                            handleUserProfileSettingsSidebar={handleUserProfileSettingsSidebar}
                            handleNotificationSidebar={handleNotificationSidebar}
                            selectedFiltersChanged={selectedFiltersChanged}
                            filters={filters}
                            filterNames={filterNames}
                            hasUnreadNotifications={unreadNotifications.length > 0}
                            errorInQueryParams={errorInQueryParams ?? false}
                          />
                        )}
                        {!limitedAccess && (
                          <>
                            <WebframePage
                              handleHelpSectionSidebar={handleHelpSectionSidebar}
                              handleUserProfileSettingsSidebar={handleUserProfileSettingsSidebar}
                              handleNotificationSidebar={handleNotificationSidebar}
                              selectedFiltersChanged={selectedFiltersChanged}
                              filters={filters}
                              filterNames={filterNames}
                              hasUnreadNotifications={unreadNotifications.length > 0}
                              errorInQueryParams={errorInQueryParams ?? false}
                            />
                            {showCookieBannerModal && (
                              <CookieBannerModal
                                open={showCookieSettings}
                                closeModal={(val?: boolean) => handleCookieSettingsModal(val)}
                                canClose={true}
                              />
                            )}
                          </>
                        )}
                      </Sidebar.Pusher>
                    </div>
                  </>
                ) : (
                  <NoAccess />
                )}
              </div>
            </ThemesProvider>
          </FeatureFlagsProvider>
        </NotificationContext.Provider>
      )}
    </>
  );
}

export default App;
